import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTableUniverse } from '~Components/TableUniverse';
import { useI18n, useSelector } from '~Hooks';
import { DateTime } from 'luxon';
import { gql, useQuery } from '@apollo/client';
import Loader from '~ComponentsImproved/Loader';
import { Loading } from '~Scenes/Venues/styles';
import { GetChequeLogs, GetTableLogs } from './__queries__';

const Wrapper = styled.div`
  background-color: #0e1216;
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  color: #fff;
  padding: 16px 16px 8px;
  overflow: hidden;
  position: relative;
`;

const ItemsWrapper = styled.div`
  height: calc(100% - 16px);
  overflow: auto;
  padding-bottom: 60px;
  box-sizing: border-box;

  &:after {
    content: '';
    background: linear-gradient(to top, rgba(14, 18, 22, 1) 0%, rgba(14, 18, 22, 0) 100%);
    height: 60px;
    width: 264px;
    position: fixed;
    bottom: -2px;
    right: 0;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Head = styled.div`
  padding-bottom: 10px;
  border-bottom: 1px solid #41474f;
  color: #666b71;

  span:first-child {
    width: 50px;
    margin-right: 8px;
    display: inline-block;
  }
`;

const LogRow = styled.div<{ highlight: boolean; touching: boolean }>`
  list-style: none;
  display: flex;
  border-bottom: 1px solid #1c232c;
  padding: 10px 0;
  background: ${(props) => (props.touching ? 'rgba(255,255,255,0.1)' : 'transparent')};
  opacity: ${(props) => (props.highlight ? '1' : '.6')};
`;

const Time = styled.span`
  width: 50px;
  margin-right: 8px;
  display: inline-block;
  min-width: 50px;
`;

const ModeToggle = styled.div`
  display: flex;
  margin-bottom: 16px;
`;
const Mode = styled.div<{ active: boolean }>`
  flex: 1;
  text-align: center;
  padding: 8px 0;
  color: ${(props) => (props.active ? '#fff' : '#666b71')};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  text-decoration: ${(props) => (props.active ? 'underline' : 'none')};
`;

const ExpandArrow = styled.span`
  margin-left: auto;
  color: #666b71;
  font-size: 16px;
  position: relative;
  top: -3px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 24px;
  cursor: pointer;
`;

const LogDetails = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

const LogDetailsInner = styled.div`
  margin: 15px;
  background-color: #1c232c;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  padding: 16px;
  border-radius: 8px;
  z-index: 1;
  font-size: 14px;
  font-weight: 400;
  color: #fff;
`;

const CHEQUE_QUERY = gql`
  query GetChequeLogs($chequeId: String!) {
    ChequeLogs(id: $chequeId) {
      id
      chequeId
      createdAt
      title
      message
      tableId
      staffId
    }
  }
`;

const TABLE_QUERY = gql`
  query GetTableLogs($tableId: ID!) {
    Table(id: $tableId) {
      id
      logs {
        id
        createdAt
        title
        message
        staffId
        chequeId
        tableId
      }
    }
  }
`;

interface Props {
  selectedCheque: string | null;
  table: {
    id: string;
    name: string;
  } | null;
}

const Logs: React.FunctionComponent<Props> = ({ selectedCheque, table }) => {
  const { i18n } = useI18n();

  const [mode, setMode] = useState<'table' | 'cheque'>('table');

  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  const [touchingLogId, setTouchingLogId] = useState<string | null>(null);

  const staffs = useSelector((state) => state.companyStaff);

  const chequeLogsQuery = useQuery<GetChequeLogs>(CHEQUE_QUERY, {
    variables: { chequeId: selectedCheque },
    skip: !selectedCheque,
    fetchPolicy: 'network-only',
    pollInterval: 3000,
  });

  const tableLogsQuery = useQuery<GetTableLogs>(TABLE_QUERY, {
    variables: { tableId: table?.id },
    skip: !table,
    fetchPolicy: 'network-only',
    pollInterval: 3000,
  });

  useEffect(() => {
    chequeLogsQuery.refetch();
    tableLogsQuery.refetch();
  }, [mode]);

  const logs =
    mode === 'cheque' ? chequeLogsQuery.data?.ChequeLogs : tableLogsQuery.data?.Table?.logs;
  const loading = mode === 'cheque' ? chequeLogsQuery.loading : tableLogsQuery.loading;
  const error = mode === 'cheque' ? chequeLogsQuery.error : tableLogsQuery.error;
  const logsError = error ? error.message : null;

  const expandedLog = logs?.find((log) => log.id === expandedLogId);

  return (
    <Wrapper>
      <ModeToggle>
        <Mode onClick={() => setMode('table')} active={mode === 'table'}>
          {i18n('TM.Table')
            .charAt(0)
            .toUpperCase() + i18n('TM.Table').slice(1)}
        </Mode>
        <Mode onClick={() => setMode('cheque')} active={mode === 'cheque'}>
          {i18n('TM.Bill')}
        </Mode>
      </ModeToggle>
      <Head>
        <span>{i18n('TM.Time')}</span>
        <span>{i18n('TM.Activity')}</span>
      </Head>
      <ItemsWrapper>
        {loading && (
          <div
            style={{
              width: '30px',
              margin: '0 auto',
              marginTop: '50px',
            }}>
            <Loader size="sm" color="rgba(255,255,255,0.7)" />
          </div>
        )}
        {logs?.map((log, i) => {
          const time = DateTime.fromISO(log.createdAt).toFormat('HH:mm');
          return (
            <LogRow
              highlight={log.chequeId === selectedCheque}
              touching={touchingLogId === log.id}
              key={i}
              onTouchStart={() => setTouchingLogId(log.id)}
              onTouchEnd={() => {
                setTouchingLogId(null);
              }}
              onClick={() => setExpandedLogId(log.id)}>
              <Time>{time}</Time>
              {log.title}

              {log.message && <ExpandArrow>{'>'}</ExpandArrow>}
            </LogRow>
          );
        })}
      </ItemsWrapper>
      {expandedLog && (
        <LogDetails onClick={() => setExpandedLogId(null)}>
          <LogDetailsInner>
            <strong>{expandedLog.title}</strong>
            <br />
            <br />
            {expandedLog.staffId && (
              <>
                {staffs?.staffs.find((staff) => staff.id === expandedLog.staffId)?.name}
                <br />
                <br />
              </>
            )}
            {DateTime.fromISO(expandedLog.createdAt).toFormat('dd LLL yyyy HH:mm')}
            <br />
            <br />
            <span
              style={{
                whiteSpace: 'pre-line',
                overflowWrap: 'break-word',
              }}>
              {expandedLog.message}
            </span>
          </LogDetailsInner>
        </LogDetails>
      )}
    </Wrapper>
  );
};

export default Logs;
